import { UnitTransferRequestStatusEnumType } from '__generated__/graphql';

import { UnitTransferViewerRole } from './unit-transfer-stage';

/**
 * The one sentence every user of a multi-party workflow actually wants: what happens next, and
 * whether they are the one holding it up. Copy differs by viewer — the same PENDING_DOCUMENTS
 * status is "your turn to upload" for the transferor and "waiting on them" for the transferee.
 *
 * `tone` maps 1:1 onto primitives/Alert's `type`, so callers never pick colours themselves.
 */

export type UnitTransferNextAction = {
  tone: 'info' | 'success' | 'warning' | 'error';
  title: string;
  body: string;
  /** Set only for navigations. Mutations stay as the action buttons in the page header. */
  ctaLabel?: string;
  ctaPath?: string;
};

type NextActionContext = {
  status: UnitTransferRequestStatusEnumType;
  viewerRole: UnitTransferViewerRole;
  counterpartyName: string;
  requestId: string;
};

export default function getUnitTransferNextAction({
  status,
  viewerRole,
  counterpartyName,
  requestId,
}: NextActionContext): UnitTransferNextAction | null {
  const viewerIsTransferor = viewerRole === 'TRANSFEROR';

  switch (status) {
    case UnitTransferRequestStatusEnumType.Draft:
      return viewerIsTransferor
        ? {
            tone: 'warning',
            title: 'Your turn — submit this request',
            body: `This request is still a draft. Submit it using the button above and we'll ask ${counterpartyName} to accept it.`,
          }
        : {
            tone: 'info',
            title: 'Not submitted yet',
            body: `${counterpartyName} is still preparing this request. You'll be asked to respond once it's submitted.`,
          };

    case UnitTransferRequestStatusEnumType.PendingTransfereeAcknowledgement:
      return viewerIsTransferor
        ? {
            tone: 'info',
            title: `Waiting on ${counterpartyName}`,
            body: "We've asked them to review this transfer and confirm they accept the units. Nothing is needed from you until they respond.",
          }
        : {
            tone: 'warning',
            title: 'Your turn — review and respond',
            body: `${counterpartyName} wants to transfer their units to you. Review the details and choose the entity that should receive them.`,
            ctaLabel: 'Review and respond',
            ctaPath: `/invest/unit-transfers/${requestId}/acknowledge`,
          };

    case UnitTransferRequestStatusEnumType.Submitted:
      return {
        tone: 'info',
        title: 'With our team',
        body: 'Both parties have confirmed. Our operations team will pick this up for review shortly.',
      };

    case UnitTransferRequestStatusEnumType.UnderReview:
      return {
        tone: 'info',
        title: 'Under review by AngelList India',
        body: "We're checking eligibility and working out which documents this transfer needs. You'll get a checklist once that's done.",
      };

    case UnitTransferRequestStatusEnumType.PendingEscrowException:
      return {
        tone: 'info',
        title: 'Escrow exception under review',
        body: 'Your request to transfer directly from the AngelList India Trust escrow account needs sign-off from both our operations and legal teams.',
      };

    case UnitTransferRequestStatusEnumType.ConditionsIssued:
    case UnitTransferRequestStatusEnumType.PendingDocuments:
      return viewerIsTransferor
        ? {
            tone: 'warning',
            title: 'Your turn — upload documents',
            body: "We've issued the checklist for this transfer. Upload each document listed as yours, then submit them for review.",
            ctaLabel: 'Upload documents',
            ctaPath: `/invest/unit-transfers/${requestId}/documents`,
          }
        : {
            tone: 'info',
            title: `Waiting on ${counterpartyName}`,
            body: 'They need to upload the documents this transfer requires before it can move to signing.',
          };

    case UnitTransferRequestStatusEnumType.DocumentsIncomplete:
      return viewerIsTransferor
        ? {
            tone: 'error',
            title: 'Some documents need your attention',
            body: 'One or more documents were sent back. Open the checklist to see why, then re-upload them and submit again.',
            ctaLabel: 'Fix documents',
            ctaPath: `/invest/unit-transfers/${requestId}/documents`,
          }
        : {
            tone: 'info',
            title: `Waiting on ${counterpartyName}`,
            body: 'Some of their documents were sent back and need to be re-uploaded.',
          };

    case UnitTransferRequestStatusEnumType.DocumentsUnderReview:
      return {
        tone: 'info',
        title: 'Documents with our team',
        body: "We're reviewing everything that was uploaded. We'll let you know if anything needs to change.",
      };

    case UnitTransferRequestStatusEnumType.DocumentsVerified:
      return {
        tone: 'success',
        title: 'Documents verified',
        body: "Everything checks out. We're preparing the transfer documents for signing — you'll get an email when it's your turn to sign.",
      };

    case UnitTransferRequestStatusEnumType.PendingSignatures:
      return {
        tone: 'warning',
        title: 'Signatures in progress',
        body: 'The transfer documents are being signed in order by each party. You will receive an email with a signing link when it reaches you.',
      };

    case UnitTransferRequestStatusEnumType.Executed:
      return {
        tone: 'success',
        title: 'Transfer executed',
        body: 'Every document has been signed. Our team is preparing the depository instruction needed to move the units.',
      };

    case UnitTransferRequestStatusEnumType.ReadyForDis:
      return {
        tone: 'success',
        title: 'Ready for depository filing',
        body: 'This transfer is complete on our side. The remaining depository steps are handled offline by our operations team.',
      };

    case UnitTransferRequestStatusEnumType.Rejected:
      return {
        tone: 'error',
        title: 'Request rejected',
        body: 'Our team could not proceed with this transfer. The reason is recorded in the history below.',
      };

    case UnitTransferRequestStatusEnumType.Withdrawn:
      return {
        tone: 'info',
        title: 'Request withdrawn',
        body: 'This request was withdrawn and is no longer active. You can start a new transfer request if you still need one.',
      };

    case UnitTransferRequestStatusEnumType.TransfereeDeclined:
      return viewerIsTransferor
        ? {
            tone: 'error',
            title: 'Transferee declined',
            body: `${counterpartyName} declined this transfer. Declining is final — raise a new request if you still want to transfer your units.`,
          }
        : {
            tone: 'info',
            title: 'You declined this transfer',
            body: 'No units were transferred. The transferor will need to raise a new request if they want to try again.',
          };

    default:
      return null;
  }
}
